import { useState } from 'react';
import useSWR from 'swr';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
 HStack,
 Center,
 Container,
 useToast,
 Grid,
 Checkbox,
 Box,
 Tabs,
 TabList,
 TabPanels,
 TabPanel,
 Heading,
 Flex,
} from '@chakra-ui/react';
import { DatePicker, InputText, Select } from '../../../../components/Input';
import { QuestionnaireHeaderNew } from '../../../../components/Form';
import { periodicityOptions } from '../../../../resources/mock_options';
import baseService from '../../../../services/base';
import { SuccessModal } from '../../../../components/Modals';
import { CustomTab } from '../../../../components/Tabs';
import PillarTabEdit from './PillarTabEdit';
import { fetchData } from '../../../../services/fetch';
import { periodicEditSchema } from '../../../../core/validations/periodic';
import { format } from 'date-fns';

export default function PeriodicEditForm({ defaultValues, data, mutate }) {
 const toast = useToast();
 const navigate = useNavigate();
 const { id: surveyId } = useParams();

 const [tabIndex, setTabIndex] = useState(0);
 const [startDeadline, setStartDeadline] = useState(defaultValues?.startDeadline);
 const [endDeadline, setEndDeadline] = useState(defaultValues?.endDeadline || null);
 const [noEndDeadline, setNoEndDeadline] = useState(false);
 const [selectedPeriodicity, setSelectedPeriodicity] = useState(
  data?.period?.type_period
 );
 const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

 const {
  handleSubmit,
  register,
  control,
  trigger,
  setValue,
  formState: { errors, isValid, isDirty, isSubmitting },
 } = useForm({
  defaultValues,
  resolver: yupResolver(periodicEditSchema),
  mode: 'onChange',
 });

 const { fields } = useFieldArray({
  control,
  name: 'form',
 });

 const { data: templates, isLoading: templatesIsLoading } = useSWR(
  `/survey-template/all`,
  fetchData
 );

 const handleTabsChange = (index) => {
  setTabIndex(index);
 };

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const onSubmit = async (values) => {
  const {
   name,
   form,
   startDeadline: startDate,
   endDeadline: endDate,
   noEndDeadline,
  } = values;
  try {
   if (
    name !== defaultValues?.name ||
    noEndDeadline !== defaultValues?.noEndDeadline ||
    startDate !== startDeadline ||
    endDate !== endDeadline
   ) {
    const start = format(new Date(startDate), 'yyyy-MM');

    await baseService.patch(`/survey/periodic/${surveyId}`, {
     description: name,
     start_date: start,
     ...(!noEndDeadline && {
      end_date: format(new Date(values.endDeadline), 'yyyy-MM'),
     }),
     type_period: selectedPeriodicity,
    });
   }

   // EG-42/AD-5: o laço que apagava pilares SAIU.
   //
   // Pós-EG-40 o pilar é do CATÁLOGO GLOBAL — a rota DELETE apagava a linha do
   // catálogo (e, no caso de /survey/pillar/:id, todos os temas dela) a partir da
   // tela de UM questionário, tirando o conceito de todos os clientes que o
   // usam. Não é dívida técnica: era perda de dado em produção.
   //
   // E, pela AD-9, remover não existe mais como operação: a classificação da
   // questão dentro do questionário é imutável em existência — tem pilar, pode
   // TROCAR; não tem, não ganha. Apagar do catálogo para "desvincular" nunca foi
   // a mesma coisa. Quem precisa aposentar um pilar faz isso na tela de gestão do
   // catálogo, em /pillars, onde apagar é legítimo.


   for (const pillarData of form) {
    const { pillarId, idPillar, themes } = pillarData;

    const defaultPillarData = defaultValues?.form?.find(
     (item) => item?.pillarId === pillarId
    );

    for (const theme of themes) {
     const defaultTheme = defaultPillarData?.themes.find(
      (item) => item.index === theme.index
     );

     if (defaultTheme) {
      const originalQuestionIds = defaultTheme?.questions.map(
       (q) => q.id_question_template
      );
      const updatedQuestionIds = theme?.questions.map((q) => q.id_question_template);

      const removedQuestions = originalQuestionIds.filter(
       (id) => !updatedQuestionIds.includes(id)
      );

      if (removedQuestions.length > 0) {
       if (!theme.delete) {
        theme.delete = [];
       }
       theme.delete.push(...removedQuestions);

       // Aqui, o campo questions deve permanecer como está
       theme.questions = updatedQuestionIds;
      } else {
       // Caso removedQuestions seja falso, convertemos theme.questions para uma array de números
       theme.questions = updatedQuestionIds;
      }
     } else {
      // Se não houver defaultTheme, ainda assim precisamos garantir que questions seja uma array de números
      theme.questions = theme.questions.map((q) => q.id_question_template);
     }
    }

    const formValues = {
     id_survey: +surveyId,
     id_pillar: idPillar,
     theme: themes,
    };

    if (pillarData?.pillarId) {
     await baseService.patch(`/survey/periodic/pillar`, {
      id_pillar: pillarId,
      ...formValues,
     });
    } else {
     await baseService.post(
      `/survey/periodic/pillar`,
      JSON.stringify({
       ...formValues,
      })
     );
    }
   }
   setIsSuccessModalOpen(true);
  } catch (error) {
   showErrorToast('Não foi possível salvar o questionário');
  }
 };

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <QuestionnaireHeaderNew
    heading='Editar Questionário'
    isSubmitEnabled={isDirty && isValid && selectedPeriodicity}
    isSubmitting={isSubmitting}
    isSubmit={true}
   >
    <Flex gap='4' flexWrap='wrap' alignItems='baseline'>
     <HStack minW='360px' w='500px'>
      <InputText
       {...register('name')}
       error={errors.name}
       placeholder='Digite o nome do questionário'
       autoComplete='off'
      />
     </HStack>

     <Grid templateColumns='220px 220px 220px 140px' gap={4}>
      <Select
       name='periodicity'
       placeholder='Tipo de periodicidade'
       options={periodicityOptions}
       defaultValue={selectedPeriodicity}
       onChange={(e) => setSelectedPeriodicity(e.target.value)}
      />
      <DatePicker
       control={control}
       name='startDeadline'
       defaultValue={startDeadline}
       placeholder='Prazo inicial'
       selectedDate={startDeadline}
       onChange={(date) => {
        setStartDeadline(date);
        trigger('endDeadline');
       }}
       disabled={!selectedPeriodicity}
      />
      <DatePicker
       control={control}
       name='endDeadline'
       defaultValue={endDeadline}
       placeholder='Prazo final'
       selectedDate={endDeadline}
       minDate={startDeadline}
       onChange={(date) => setEndDeadline(date)}
       disabled={!selectedPeriodicity || noEndDeadline || !startDeadline}
       error={errors.endDeadline}
      />
      <Checkbox
       {...register('noEndDeadline')}
       onChange={(e) => {
        setNoEndDeadline(e.target.checked);
        setValue('noEndDeadline', e.target.checked);
        trigger('endDeadline');
       }}
       className='regular-checkbox'
      >
       Sem prazo final
      </Checkbox>
     </Grid>
    </Flex>
   </QuestionnaireHeaderNew>

   <Center my={['8px', '32px']} px={{ xl: '86px' }}>
    <Container maxW={'1280px'} p={0} minH='80vh'>
     <Box bg='white' py='6' borderRadius='12px'>
      <Tabs index={tabIndex} onChange={handleTabsChange}>
       <TabList px='6' border='none' userSelect='none'>
        {/* EG-42: a aba "Configurações gerais" saiu. Ela continha o "Número de
            pilares", o seletor de pilar e o "Número de temas" — os três descrevem
            um formulário que não existe mais: a estrutura vem do template
            (decisão do TL, 2026-08-07). */}
        {/* EG-42: a aba mostra o NOME do pilar, como na certificação. A EG-40
            tinha caído para a posição ("Pilar 1") ao tirar o nome digitado — mas
            o nome existe: vem do catálogo global. */}
        {fields?.map(
         (field, index) =>
          !field?.hidden && (
           <CustomTab key={field.id}>
            {field?.pillarName || `Pilar ${index + 1}`}
           </CustomTab>
          )
        )}
       </TabList>
       <TabPanels>

        {fields?.map(
         (field, index) =>
          !field?.hidden && (
           <TabPanel key={field.id} py='6' px='0'>
            <Heading
             fontSize='20px'
             fontWeight={600}
             lineHeight='23.48px'
             p='6'
             maxH='71px'
             borderBlock='2px solid #F5F5FB'
            >
             {field?.pillarName || `Pilar ${index + 1}`}
            </Heading>
            <PillarTabEdit
             index={index}
             field={field}
             register={register}
             errors={errors}
             control={control}
             templates={templates}
             templatesIsLoading={templatesIsLoading}
            />
           </TabPanel>
          )
        )}
       </TabPanels>
      </Tabs>
     </Box>
     <SuccessModal
      isOpen={isSuccessModalOpen}
      onClose={() => {
       setIsSuccessModalOpen(false);
       mutate();
       navigate('/researches');
      }}
      title='Questionário criado com sucesso!'
     />
    </Container>
   </Center>
  </form>
 );
}
